import { getCountry } from "countries-and-timezones"
import { allCountries, Country } from "country-telephone-data"

type CustomCountry = Country & { code: string; timezones?: string[] }

export function listCountries(locale: string = "en") {
  const lang = locale.split(/[-_]/)[0] || "en"

  try {
    const displayNames = new Intl.DisplayNames([lang], { type: "region" })
    const list = allCountries.map((country) => ({
      name: displayNames.of(country.iso2.toUpperCase()) || country.name,
      code: country.iso2.toUpperCase(),
      dialCode: country.dialCode,
    }))

    return list.sort((a, b) => a.name.localeCompare(b.name, lang))
  } catch (error) {
    // Fallback if Intl.DisplayNames is not supported or locale is invalid
    throw new Error(`Failed to get country names for locale "${lang}": ${error}`)
  }
}

export function mapCountries(lang: string = "en") {
  return listCountries(lang).reduce<Record<string, string>>((acc, country) => {
    acc[country.code] = country.name
    return acc
  }, {})
}

export function parseCountry(code: string, withTimezones: boolean = false): CustomCountry | null {
  const countryMap = allCountries.reduce<Record<string, Country>>((acc, country) => {
    acc[country.iso2.toUpperCase()] = country
    return acc
  }, {})

  const country =
    countryMap[code.toUpperCase()] ??
    allCountries.find((country) => country.name.includes(code)) ??
    null

  let timezones: string[] | undefined
  if (country && withTimezones) {
    const countryWithTimezones = getCountry(country.iso2.toUpperCase())
    timezones = countryWithTimezones?.timezones
  }

  if (!country) return null

  return {
    ...country,
    timezones,
    code: country.iso2.toUpperCase(),
  }
}

export function getCountryPhoneCode(countryCode: string) {
  const countryPhoneCodeMap = allCountries.reduce<Record<string, string>>((acc, country) => {
    acc[country.iso2.toUpperCase()] = country.dialCode
    return acc
  }, {})

  return countryPhoneCodeMap[countryCode.toUpperCase()] ?? null
}

export function getPhoneCodeCountry(phoneCode: string) {
  const phoneCodeCountryMap = allCountries.reduce<Record<string, Country>>((acc, country) => {
    acc[country.dialCode] = country
    return acc
  }, {})

  return phoneCodeCountryMap[phoneCode] ?? null
}

export function getWhatsappPhoneWithCountryCode(phone: string, code: string) {
  return [getCountryPhoneCode(code), phone.replace(/\s/g, "")].join("")
}

const WORKING_HOURS_START = 8
const WORKING_HOURS_END = 22

export const getHourInTimezone = (date: Date, tz: string): number | undefined => {
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      hour: "numeric",
      hourCycle: "h23",
    }).formatToParts(date)
    let hour
    for (const part of parts) {
      if (part.type === "hour") {
        hour = parseInt(part.value, 10)
        break
      }
    }
    if (hour === 24) return 0
    return hour
  } catch (error) {
    return undefined
  }
}

export const getDayInTimezone = (date: Date, tz: string): number | undefined => {
  try {
    const dayStr = new Intl.DateTimeFormat("en-US", { timeZone: tz, weekday: "long" })
      .format(date)
      .toLowerCase()
    const days: Record<string, number> = {
      sunday: 0,
      monday: 1,
      tuesday: 2,
      wednesday: 3,
      thursday: 4,
      friday: 5,
      saturday: 6,
    }
    return days[dayStr]
  } catch (error) {
    return undefined
  }
}

export const isOutsideWorkingHours = (
  date: Date,
  timezones: readonly string[] | undefined,
): boolean => {
  const tzToCheck = !timezones || timezones.length === 0 ? ["UTC"] : timezones

  // NOTE: For countries with multiple timezones, we consider it working hours if it's daytime in ANY of the timezones.
  const isWorkingHoursInSomeTimezone = tzToCheck.some((tz) => {
    const hour = getHourInTimezone(date, tz)
    return hour !== undefined && hour >= WORKING_HOURS_START && hour < WORKING_HOURS_END
  })

  return !isWorkingHoursInSomeTimezone
}

export const getHoursUntilNextWorkingDay = (
  date: Date,
  timezones: readonly string[] | undefined,
): number => {
  let maxHoursToWait = 0
  const tzToCheck = !timezones || timezones.length === 0 ? ["UTC"] : timezones

  for (const tz of tzToCheck) {
    const hour = getHourInTimezone(date, tz)
    const day = getDayInTimezone(date, tz)
    if (hour === undefined || day === undefined) {
      continue
    }

    let hoursToWait = 0
    if (hour >= WORKING_HOURS_START && hour < WORKING_HOURS_END) {
      hoursToWait = 0
    } else if (hour < WORKING_HOURS_START) {
      hoursToWait = WORKING_HOURS_START - hour
    } else {
      const hoursLeftInToday = 24 - hour
      hoursToWait = hoursLeftInToday + WORKING_HOURS_START
    }

    maxHoursToWait = Math.max(maxHoursToWait, hoursToWait)
  }

  // Cap at three days to prevent unreasonable values
  return Math.min(maxHoursToWait, 72)
}
